import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import LangSwitch from "@/components/LangSwitch";
import ThemeSwitch from "@/components/ThemeSwitch";
import { Button } from "@/components/ui/button";
import { Check, Phone, Loader2 } from "lucide-react";
import Marque from "../components/Marque";
import { toast } from "sonner";
import Faq from "@/components/Faq";
import { evenement } from "@/lib/pixel";
import { evenementGA } from "@/lib/analytics";

// Grille de secours si l'API ne répond pas (mêmes valeurs que PLANS côté serveur).
const PLANS_DEFAUT = [
  { id: "solo", nom_fr: "Solo", nom_en: "Solo", prix_mois: 119, prix_annuel_mois: 88, estimates_per_month: 40, surplus: 1.6, prix_mois_usd: 79, prix_annuel_mois_usd: 59, surplus_usd: 1.5 },
  { id: "team", nom_fr: "Team", nom_en: "Team", prix_mois: 199, prix_annuel_mois: 129, estimates_per_month: 200, surplus: 1, prix_mois_usd: 129, prix_annuel_mois_usd: 89, surplus_usd: 1 },
];

const CONTACT = "quotes@roofborder.com";
// Numéro sans frais : « vanity » à l'affichage, chiffres réels dans le lien tel:.
const TEL_AFFICHE = "1-866-4ROOF-21";
const TEL_LIEN = "tel:+18664766321";

// Textes de la page dans les trois langues (même patron que Accueil.js).
// Les entrées paramétrées sont des fonctions ; tout le reste est une chaîne.
const T = {
  en: {
    connexion: "Sign in",
    surtitre: "Plans",
    h1: "A roof priced in under a minute",
    sousH1: "Enter an address: the system measures the roof by satellite, applies your prices and produces a branded PDF. Answer first, close more often.",
    mensuel: "Monthly",
    annuel: "Billed yearly",
    annuelBadge: "up to 30% off",
    codeLabel: "Referral or offer code (optional)",
    codeValide: (p) => `Valid code — ${p.rabais}% off for ${p.mois} months${p.entreprise ? `, courtesy of ${p.entreprise}` : ""}. On a yearly plan, the discount applies to the whole year.`,
    codeEssai: (p) => `Valid code — ${p.restantes} seat(s) left. You get $${p.montant_usd} off your first month: the Solo plan comes to zero, and a bigger plan costs only the difference. Cancel any time.`,
    codeEpuise: (p) => `All free seats are taken — but your code still gives you ${p.rabais}% off, for as long as you stay subscribed.`,
    codeInconnu: "This code isn't recognized.",
    codeAide: "A roofer referred you to Roof Border? Their code gets you 20% off.",
    paiementIndispo: "Online payment unavailable — write to us.",
    paiementAnnule: "Payment cancelled — nothing was charged.",
    populaire: "Popular",
    parMois: "mo",
    factureAnnuel: (mois) => `billed yearly (instead of ${mois}/mo)`,
    ouAnnuel: (mois) => `or ${mois}/mo billed yearly`,
    // Listes ETAGEES (regle de Jeff 2026-08-03) : Solo a un point de moins que
    // Trio, qui en a un de moins que Team (7/8/9). Les items
    // { t, fort } ressortent en gras : ce sont les differenciateurs.
    inclus: (p, surplus, parMesure, depot) => {
      // À la Carte : QUE des arguments positifs (regle de Jeff). La seule
      // absence qu'on affiche est celle qui se vend : aucun frais mensuel.
      if (p.a_la_carte) return [
        { t: "2 extra measurements on your first purchase", fort: true },
        { t: `${parMesure} per satellite measurement`, fort: true },
        "Measurements saved — reopen them anytime",
        `Top up from ${depot}, no subscription`,
      ];
      const base = [
        { t: `${p.estimates_per_month} satellite estimates / month`, fort: true },
        "Automatic satellite measurement — or enter your own, free of quota",
        "Branded PDF quotes, your prices and colors",
        "Estimate widget for your website — a lead machine",
        "Lead manager with notes and CSV export",
      ];
      // Le tarif de dépassement baisse à chaque palier : en gras, il se compare
      // d'une carte à l'autre.
      const extra = { t: `${surplus} / additional estimate`, fort: true };
      const plus = [...base,
        { t: "Automatic lead follow-up, on or off per lead", fort: true },
        { t: "5-step follow-up sequence, on your schedule", fort: true },
      ];
      if (p.id === "solo" || p.id === "trio") return [...plus, extra];
      return [...plus, { t: "Salespeople assigned and printed on your quotes", fort: true }, extra];
    },
    commencer: "Get started",
    carteParMesure: "measurement",
    carteSansAbo: "No subscription — you only pay for what you measure.",
    carteMontant: (depot) => `Amount to load (min. ${depot})`,
    carteBtn: "Load my account",
    carteBonus: "First purchase: 2 extra measurements, on us — your first $10 buys three. Spend one on a roof you already measured, then compare.",
    carteErreur: "Could not start the payment. Try again in a moment.",
    mentions: `Prices in Canadian dollars. Secure payment through Stripe, cancel anytime. Your account opens as soon as the payment clears.`,
    mentionsUsd: `Prices in US dollars. Secure payment through Stripe, cancel anytime. Your account opens as soon as the payment clears.`,
    deviseCad: "$ CAN",
    deviseUsd: "US$",
  },
  fr: {
    connexion: "Connexion",
    surtitre: "Forfaits",
    h1: "Un prix de toiture en moins d'une minute",
    sousH1: "Entrez une adresse : le système mesure le toit par satellite, applique vos prix et produit un PDF à votre image. Répondez le premier, signez plus souvent.",
    mensuel: "Mensuel",
    annuel: "Payé à l'année",
    annuelBadge: "jusqu'à -30 %",
    codeLabel: "Code de parrainage ou d'offre (facultatif)",
    codeValide: (p) => `Code valide — ${p.rabais} % de rabais pendant ${p.mois} mois${p.entreprise ? `, gracieuseté de ${p.entreprise}` : ""}. Sur un forfait annuel, le rabais porte sur l'année complète.`,
    codeEssai: (p) => `Code valide — il reste ${p.restantes} place(s). Vous obtenez ${p.montant_cad} $ de rabais sur votre premier mois : le forfait Solo tombe à zéro, et un forfait plus gros ne vous coûte que la différence. Annulable en tout temps.`,
    codeEpuise: (p) => `Toutes les places gratuites sont prises — mais votre code vous donne quand même ${p.rabais} % de rabais, tant que vous restez abonné.`,
    codeInconnu: "Ce code n'est pas reconnu.",
    codeAide: "Un couvreur vous a recommandé Roof Border ? Son code vous donne 20 % de rabais.",
    paiementIndispo: "Paiement en ligne indisponible — écrivez-nous.",
    paiementAnnule: "Paiement annulé — rien n'a été facturé.",
    populaire: "Populaire",
    parMois: "mois",
    factureAnnuel: (mois) => `facturé annuellement (au lieu de ${mois}/mois)`,
    ouAnnuel: (mois) => `ou ${mois}/mois payé à l'année`,
    inclus: (p, surplus, parMesure, depot) => {
      if (p.a_la_carte) return [
        { t: "2 mesures offertes dès votre premier dépôt", fort: true },
        { t: `${parMesure} par mesure satellite`, fort: true },
        "Mesures enregistrées — à rouvrir quand vous voulez",
        `Rechargez à partir de ${depot}, sans abonnement`,
      ];
      const base = [
        { t: `${p.estimates_per_month} estimations satellite / mois`, fort: true },
        "Mesure satellite automatique — ou saisissez les vôtres, hors quota",
        "Soumissions PDF à votre image, vos prix, vos couleurs",
        "Widget d'estimation pour votre site web — une machine à prospects",
        "Gestion des prospects avec notes et export Excel",
      ];
      const extra = { t: `${surplus} / estimation additionnelle`, fort: true };
      const plus = [...base,
        { t: "Relance automatique, activable prospect par prospect", fort: true },
        { t: "Séquence de 5 suivis programmés, à vos délais", fort: true },
      ];
      if (p.id === "solo" || p.id === "trio") return [...plus, extra];
      return [...plus, { t: "Vendeurs assignés, imprimés sur vos soumissions", fort: true }, extra];
    },
    commencer: "Commencer",
    carteParMesure: "mesure",
    carteSansAbo: "Sans abonnement — vous ne payez que ce que vous mesurez.",
    carteMontant: (depot) => `Montant à déposer (min. ${depot})`,
    carteBtn: "Créditer mon compte",
    carteBonus: "Premier achat : 2 mesures de plus, offertes — vos premiers 14 $ en donnent trois. Prenez-en une sur un toit que vous avez déjà mesuré, puis comparez.",
    carteErreur: "Impossible de lancer le paiement. Réessayez dans un instant.",
    mentions: `Prix en dollars canadiens. Paiement sécurisé par Stripe, annulable en tout temps. Votre compte s'ouvre dès le paiement confirmé.`,
    mentionsUsd: `Prix en dollars américains. Paiement sécurisé par Stripe, annulable en tout temps. Votre compte s'ouvre dès le paiement confirmé.`,
    deviseCad: "$ CAN",
    deviseUsd: "$ US",
  },
  es: {
    connexion: "Iniciar sesión",
    surtitre: "Planes",
    h1: "El precio de un techo en menos de un minuto",
    sousH1: "Ingrese una dirección: el sistema mide el techo por satélite, aplica sus precios y produce un PDF con su imagen. Responda primero, cierre más contratos.",
    mensuel: "Mensual",
    annuel: "Pago anual",
    annuelBadge: "hasta 30% de descuento",
    codeLabel: "Código de referencia o de oferta (opcional)",
    codeValide: (p) => `Código válido — ${p.rabais}% de descuento durante ${p.mois} meses${p.entreprise ? `, cortesía de ${p.entreprise}` : ""}. En un plan anual, el descuento aplica al año completo.`,
    codeEssai: (p) => `Código válido — quedan ${p.restantes} lugar(es). Obtiene $${p.montant_usd} de descuento en su primer mes: el plan Solo queda en cero, y un plan mayor solo le cuesta la diferencia. Cancele cuando quiera.`,
    codeEpuise: (p) => `Todos los lugares gratuitos están tomados — pero su código igual le da ${p.rabais}% de descuento, mientras siga suscrito.`,
    codeInconnu: "Este código no se reconoce.",
    codeAide: "¿Un techador le recomendó Roof Border? Su código le da un 20% de descuento.",
    paiementIndispo: "Pago en línea no disponible — escríbanos.",
    paiementAnnule: "Pago cancelado — no se cobró nada.",
    populaire: "Popular",
    parMois: "mes",
    factureAnnuel: (mois) => `facturado anualmente (en lugar de ${mois}/mes)`,
    ouAnnuel: (mois) => `o ${mois}/mes con pago anual`,
    inclus: (p, surplus, parMesure, depot) => {
      if (p.a_la_carte) return [
        { t: "2 mediciones de regalo en su primera compra", fort: true },
        { t: `${parMesure} por medición satelital`, fort: true },
        "Mediciones guardadas — ábralas cuando quiera",
        `Recargue desde ${depot}, sin suscripción`,
      ];
      const base = [
        { t: `${p.estimates_per_month} estimados satelitales / mes`, fort: true },
        "Medición satelital automática — o ingrese las suyas, sin consumir cuota",
        "Cotizaciones PDF con su imagen, sus precios y sus colores",
        "Widget de estimados para su sitio web — una máquina de prospectos",
        "Gestión de prospectos con notas y exportación Excel",
      ];
      const extra = { t: `${surplus} / estimado adicional`, fort: true };
      const plus = [...base,
        { t: "Seguimiento automático, activable prospecto por prospecto", fort: true },
        { t: "Secuencia de 5 seguimientos programados, a sus tiempos", fort: true },
      ];
      if (p.id === "solo" || p.id === "trio") return [...plus, extra];
      return [...plus, { t: "Vendedores asignados, impresos en sus cotizaciones", fort: true }, extra];
    },
    commencer: "Empezar",
    carteParMesure: "medición",
    carteSansAbo: "Sin suscripción — solo paga lo que mide.",
    carteMontant: (depot) => `Monto a depositar (mín. ${depot})`,
    carteBtn: "Cargar mi cuenta",
    carteBonus: "Primera compra: 2 mediciones más, de regalo — sus primeros 10 $ dan tres. Use una en un techo que ya midió, y compare.",
    carteErreur: "No se pudo iniciar el pago. Inténtelo de nuevo.",
    mentions: `Precios en dólares canadienses. Pago seguro con Stripe, cancelable en cualquier momento. Su cuenta se abre en cuanto se confirma el pago.`,
    mentionsUsd: `Precios en dólares estadounidenses. Pago seguro con Stripe, cancelable en cualquier momento. Su cuenta se abre en cuanto se confirma el pago.`,
    deviseCad: "$ CAN",
    deviseUsd: "US$",
  },
};

export default function Tarifs() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const fr = lang === "fr";
  const L = T[lang] || T.en;
  const [plans, setPlans] = useState(PLANS_DEFAUT);
  const [annuel, setAnnuel] = useState(false);
  // Devise d'affichage et de facturation. Ordre de priorité :
  //   1. le clic du visiteur sur l'interrupteur $ CAN / US$ — il prime toujours ;
  //   2. son PAYS, deviné par son IP côté serveur (/public/devise) ;
  //   3. sa langue (espagnol = marché américain), le temps que la réponse arrive ;
  //   4. CAD, le marché d'origine.
  // Un couvreur du Texas ne doit pas découvrir nos prix en dollars canadiens :
  // il compare avec ce qu'il connaît, et la mauvaise monnaie se lit comme une
  // erreur de notre part.
  const [deviseChoisie, setDeviseChoisie] = useState(null);
  const [devisePays, setDevisePays] = useState(null);
  const devise = deviseChoisie || devisePays || (lang === "es" ? "usd" : "cad");
  const usd = devise === "usd";
  // Montant d'un plan dans la devise affichée (repli CAD si la grille US manque).
  const montant = (p, cle) => (usd ? (p[`${cle}_usd`] ?? p[cle]) : p[cle]);
  const [enCours, setEnCours] = useState("");
  const [code, setCode] = useState("");
  const [parrain, setParrain] = useState(null);   // null = pas vérifié

  // Pays du visiteur, une seule fois au chargement. En cas de panne ou de
  // lenteur, on garde simplement le défaut : la page ne dépend pas de ce appel.
  useEffect(() => {
    let vivant = true;
    api.get("/public/devise")
      .then(({ data }) => { if (vivant && data?.devise) setDevisePays(data.devise); })
      .catch(() => {});
    return () => { vivant = false; };
  }, []);

  // Vérification du code de parrainage, à la frappe (le lien de partage le
  // pré-remplit, mais on accepte aussi la saisie à la main).
  useEffect(() => {
    const propre = code.trim().toUpperCase();
    if (propre.length < 6) { setParrain(null); return; }
    let vivant = true;
    const minuteur = setTimeout(() => {
      api.get("/public/code", { params: { code: propre } })
        .then(({ data }) => { if (vivant) setParrain(data?.valide ? data : false); })
        .catch(() => { if (vivant) setParrain(false); });
    }, 400);
    return () => { vivant = false; clearTimeout(minuteur); };
  }, [code]);

  // Achat en ligne. Un visiteur passe par le parcours public (Stripe collecte
  // son courriel, le compte s'ouvre au paiement) ; un couvreur déjà connecté
  // passe par la route authentifiée, qui rattache l'abonnement à son compte.
  const acheter = async (planId) => {
    setEnCours(planId);
    const interval = annuel ? "year" : "month";
    // Départ vers Stripe : c'est l'intention d'achat. Le paiement confirmé, lui,
    // se compte sur /bienvenue — sinon un abandon au checkout serait une vente.
    const valeur = montant(PLANS_DEFAUT.find((p) => p.id === planId) || {},
                           annuel ? "prix_annuel_mois" : "prix_mois");
    const monnaie = devise === "usd" ? "USD" : "CAD";
    evenement("InitiateCheckout", {
      content_name: planId, content_category: interval,
      value: valeur, currency: monnaie,
    });
    evenementGA("begin_checkout", {
      value: valeur, currency: monnaie,
      items: [{ item_id: planId, item_name: planId, item_variant: interval }],
    });
    try {
      const { data } = user
        ? await api.post("/stripe/checkout", { plan: planId, interval, devise })
        : await api.post("/public/checkout", { plan: planId, interval, lang, devise, code: code.trim().toUpperCase() });
      if (!data?.url) throw new Error("no url");
      window.location.href = data.url;
    } catch {
      // Paiement en ligne indisponible : on ne laisse pas le client dans le vide.
      toast.error(L.paiementIndispo);
      window.location.href = `mailto:${CONTACT}?subject=${encodeURIComponent(`Forfait ${planId} — Roof Border`)}`;
      setEnCours("");
    }
  };

  useEffect(() => {
    // Retour d'un paiement abandonné : le dire, sinon le client croit avoir payé.
    const params = new URLSearchParams(window.location.search);
    if (params.get("paiement") === "annule") {
      toast(L.paiementAnnule);
      window.history.replaceState({}, "", window.location.pathname);
    }
    // Lien de parrainage : le code arrive dans l'URL, on le pose dans le champ.
    const recu = (params.get("code") || "").trim().toUpperCase();
    if (recu) setCode(recu);
    api.get("/plans")
      .then(({ data }) => { if (data?.plans?.length) setPlans(data.plans); })
      .catch(() => {});
    // Une seule fois au montage : le message d'annulation ne doit pas revenir
    // si le visiteur change de langue.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Format monétaire : « 120 $ » en français, « $120 » en anglais et en espagnol.
  // En dollars US, le suffixe/préfixe l'annonce sans ambiguïté.
  // Pas de « US » colle au montant (regle de Jeff, 2026-08-06) : le prix se
  // lit mieux nu. La devise reste annoncee par le selecteur du haut de page
  // et par les mentions du bas.
  const dollars = (v) => (fr ? `${v} $` : `$${v}`);
  const surplus = (v) => (fr ? `${String(v).replace(".", ",")} $` : `$${v}`);

  // À la Carte : le compte naît d'un PAIEMENT, pas d'une inscription. Le
  // visiteur choisit ce qu'il met (10 $ au minimum) ; Stripe collecte son
  // courriel, et les 2 mesures du premier achat s'ajoutent à son versement.
  // Depot de depart et minimum : le prix d'une mesure (14 $ CA, 10 $ US).
  const depotMin = usd ? 10 : 14;
  const [montantCarte, setMontantCarte] = useState(depotMin);
  const [carteEnCours, setCarteEnCours] = useState(false);

  const ouvrirCarte = async () => {
    setCarteEnCours(true);
    // Le dépôt À la Carte est un départ au paiement au même titre qu'un
    // abonnement : sans ces deux évènements, la moitié des intentions d'achat
    // serait invisible aux deux régies.
    evenement("InitiateCheckout", {
      content_name: "carte", content_category: "depot",
      value: Number(montantCarte), currency: usd ? "USD" : "CAD",
    });
    evenementGA("begin_checkout", {
      value: Number(montantCarte), currency: usd ? "USD" : "CAD",
      items: [{ item_id: "carte", item_name: "Pay as you Go" }],
    });
    try {
      const { data } = await api.post("/public/carte", {
        montant: Number(montantCarte), lang, devise: usd ? "usd" : "cad",
      });
      if (data?.url) window.location.href = data.url;
      else throw new Error();
    } catch {
      setCarteEnCours(false);
      toast.error(L.carteErreur);
    }
  };

  const inclus = (p) => L.inclus(p, surplus(montant(p, "surplus")),
                                 surplus(montant(p, "prix_mesure")),
                                 dollars(montant(p, "recharge_min")));

  return (
    <div className="min-h-screen bg-app text-ink">
      {/* entête */}
      <header className="border-b border-line">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Marque taille="md" />
          <div className="flex items-center gap-4">
            <a href={TEL_LIEN} className="hidden md:inline-flex items-center gap-1.5 text-sm font-bold text-brand-ink hover:text-brand transition-colors">
              <Phone className="h-4 w-4" /> {TEL_AFFICHE}
            </a>
            <ThemeSwitch />
            <LangSwitch langues={["en", "fr", "es"]} />
            <Link to="/login">
              <Button variant="outline" className="border-line-strong rounded-md hover:bg-hover-strong hover:text-ink h-9">
                {L.connexion}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-14">
        <div className="text-center mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-ink mb-3">{L.surtitre}</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter mb-4">
            {L.h1}
          </h1>
          <p className="text-ink-soft max-w-2xl mx-auto">
            {L.sousH1}
          </p>
        </div>

        {/* bascule de devise : $ CAN ou US$, les montants suivent */}
        <div className="flex items-center justify-center mb-4">
          <div className="inline-flex rounded-md border border-line-strong overflow-hidden" data-testid="bascule-devise">
            {[["cad", L.deviseCad], ["usd", L.deviseUsd]].map(([d, nom]) => (
              <button
                key={d}
                data-testid={`devise-${d}`}
                onClick={() => setDeviseChoisie(d)}
                className={`px-4 py-1.5 text-xs font-bold transition-colors ${
                  devise === d ? "bg-orange-600 text-white" : "bg-field text-ink-soft hover:bg-hover"
                }`}
              >
                {nom}
              </button>
            ))}
          </div>
        </div>

        {/* bascule mensuel / annuel */}
        <div className="flex items-center justify-center gap-3 mb-10">
          <button
            onClick={() => setAnnuel(false)}
            className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${!annuel ? "bg-orange-600 text-white" : "bg-hover text-ink-soft hover:bg-hover-strong hover:text-ink"}`}
          >
            {L.mensuel}
          </button>
          {/* VERT au survol ET une fois sélectionné : c'est le bouton qui fait
              économiser, et le vert est déjà la couleur de l'économie sur cette
              page (badge de rabais, forfait mis en avant).
              L'orange reste à « Mensuel », l'option ordinaire.
              `group` permet au badge de suivre le survol du bouton. */}
          <button
            onClick={() => setAnnuel(true)}
            className={`group px-4 py-2 rounded-md text-sm font-semibold transition-colors ${annuel ? "bg-emerald-600 text-white" : "bg-hover text-ink-soft hover:bg-emerald-600 hover:text-white"}`}
          >
            {L.annuel}
            {/* Le badge doit rester lisible SUR SON PROPRE FOND : blanc dès que
                le fond est plein (sélectionné ou survolé), emerald-500 au repos.
                Il était en orange sur orange, donc invisible. */}
            <span className={`ml-2 text-xs ${annuel ? "text-white/90" : "text-emerald-500 group-hover:text-white"}`}>
              {L.annuelBadge}
            </span>
          </button>
        </div>

        {/* code de parrainage */}
        <div className="max-w-md mx-auto mb-10">
          <label className="block text-xs uppercase tracking-widest text-ink-faint mb-2 text-center">
            {L.codeLabel}
          </label>
          <input
            data-testid="code-parrainage"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="RB-XXXXXX"
            maxLength={12}
            className={`w-full bg-field border rounded-md px-4 py-2.5 text-center font-mono tracking-widest outline-none transition-colors ${
              parrain === false ? "border-red-500/60" : parrain ? "border-emerald-500/60" : "border-line-strong focus:border-orange-500"
            }`}
          />
          {/* Le même champ accepte deux sortes de codes : un parrainage (rabais)
              et une offre d'essai (premier mois offert). Le serveur tranche et
              renvoie le type ; le message doit suivre, sinon un couvreur qui a
              reçu 30 jours gratuits lirait « 20 % de rabais ». */}
          {parrain && (
            <p data-testid="code-valide" className="text-sm text-emerald-500 mt-2 text-center">
              {parrain.type === "campagne" ? L.codeEssai(parrain)
                : parrain.type === "campagne_epuisee" ? L.codeEpuise(parrain)
                : L.codeValide(parrain)}
            </p>
          )}
          {parrain === false && (
            <p className="text-sm text-red-400 mt-2 text-center">
              {L.codeInconnu}
            </p>
          )}
          {parrain === null && !code && (
            <p className="text-xs text-ink-faint mt-2 text-center">
              {L.codeAide}
            </p>
          )}
        </div>

        {/* forfaits */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
          {plans.map((p) => {
            // Forfait mis en avant : Solo depuis le 2026-09-24 (demande de
            // Jeff). Il embarque désormais la relance automatique et 50 mesures,
            // donc c'est lui la vraie porte d'entrée — Team ne se distingue
            // plus que par le volume et les vendeurs assignés.
            const vedette = p.id === "solo";
            const prix = montant(p, annuel ? "prix_annuel_mois" : "prix_mois");
            return (
              // Le survol soulève la carte : sur une grille de quatre, c'est ce
              // qui dit au visiteur laquelle il est en train de comparer.
              <div
                key={p.id}
                className={`group relative rounded-xl border p-6 flex flex-col transition-all duration-200 hover:-translate-y-1 hover:shadow-releve ${
                  vedette
                    ? "border-emerald-500/60 bg-emerald-500/[0.06] hover:border-emerald-500"
                    : "border-line bg-surface hover:border-line-strong"
                }`}
              >
                {vedette && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-md shadow-carte">
                    {L.populaire}
                  </span>
                )}
                <h2 className="font-heading font-extrabold text-xl tracking-tight">{fr ? p.nom_fr : p.nom_en}</h2>
                <div className="mt-3 mb-1">
                  <span className="font-heading text-4xl font-extrabold tracking-tighter">
                    {p.a_la_carte ? surplus(montant(p, "prix_mesure")) : dollars(prix)}
                  </span>
                  <span className="text-ink-faint text-sm"> / {p.a_la_carte ? L.carteParMesure : L.parMois}</span>
                </div>
                <p className="text-xs text-ink-faint mb-5 min-h-[16px]">
                  {p.a_la_carte
                    ? L.carteSansAbo
                    : annuel
                      ? L.factureAnnuel(dollars(montant(p, "prix_mois")))
                      : L.ouAnnuel(dollars(montant(p, "prix_annuel_mois")))}
                </p>
                <ul className="space-y-2.5 text-sm text-ink-soft flex-1">
                  {inclus(p).map((li, i) => {
                    // { t, fort } = différenciateur du forfait : en gras, à
                    // l'encre pleine, pour qu'il ressorte au premier survol.
                    const fort = typeof li === "object" && li.fort;
                    const texte = typeof li === "object" ? li.t : li;
                    return (
                      <li key={i} className={`flex gap-2 ${fort ? "font-semibold text-ink" : ""}`}>
                        <Check className="h-4 w-4 text-brand-ink shrink-0 mt-0.5" />
                        {texte}
                      </li>
                    );
                  })}
                </ul>
                {p.a_la_carte ? (
                  <div className="mt-6 space-y-2">
                    <div>
                      <label className="text-xs uppercase tracking-widest text-ink-soft">
                        {L.carteMontant(dollars(depotMin))}
                      </label>
                      <input
                        type="number" min={depotMin} max={200} step="5"
                        value={montantCarte} data-testid="carte-montant"
                        onChange={(e) => setMontantCarte(e.target.value)}
                        className="mt-1 w-full h-10 px-3 rounded-md border border-line-strong bg-field text-sm focus:border-orange-500 outline-none"
                      />
                    </div>
                    <Button
                      onClick={ouvrirCarte} disabled={carteEnCours}
                      data-testid="acheter-carte"
                      className="w-full rounded-md font-semibold bg-hover-strong hover:bg-brand hover:text-white"
                    >
                      {carteEnCours && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {L.carteBtn}
                    </Button>
                    <p className="text-xs text-ink-faint">{L.carteBonus}</p>
                  </div>
                ) : (
                  <Button
                    onClick={() => acheter(p.id)}
                    disabled={!!enCours}
                    data-testid={`acheter-${p.id}`}
                    className={`mt-6 w-full rounded-md font-semibold transition-colors ${
                      vedette
                        ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                        : "bg-hover-strong hover:bg-brand hover:text-white"
                    }`}
                  >
                    {enCours === p.id && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {L.commencer}
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        <Faq className="mb-16" />

        <p className="text-center text-xs text-ink-faint">
          {usd ? L.mentionsUsd : L.mentions}
        </p>
        <div className="flex items-center justify-center gap-3 mt-4 text-xs text-ink-faint">
          <Link to="/confidentialite" className="hover:text-ink transition-colors">
            {fr ? "Confidentialité" : lang === "es" ? "Privacidad" : "Privacy"}
          </Link>
          <span>·</span>
          <Link to="/conditions" className="hover:text-ink transition-colors">
            {fr ? "Conditions" : lang === "es" ? "Términos" : "Terms"}
          </Link>
        </div>
      </main>
    </div>
  );
}
